import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useQuote } from '../context/QuoteContext';
import { Button } from '../components/ui/button';
import ProductSearchButton from '../components/ProductSearchButton';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Package, Filter, X, Search, ChevronRight, ChevronDown, ChevronUp, SlidersHorizontal, Plus } from 'lucide-react';
import axios from 'axios';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import { toast } from 'sonner';
import { productImage } from '../utils/imageUtils';
import {
  FILTER_GROUPS,
  buildFilterOptions,
  productMatchesFilters,
  activeFiltersToText,
} from '../utils/filterNormalization';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const { t, language } = useLanguage();
  const { addToQuote } = useQuote();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [expandedVariants, setExpandedVariants] = useState({});

  // Filters
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedPressureClasses, setSelectedPressureClasses] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  // Filter panel expanded sections
  const [expandedFilters, setExpandedFilters] = useState({
    category: true,
    dimension: false,
    material: false,
    pressureClass: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories/tree`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        
        // Check URL params for initial category
        const urlMainCat = searchParams.get('main_category_id');
        const urlSubCat = searchParams.get('sub_category_id');
        if (urlMainCat) {
          setSelectedCategories([urlMainCat]);
        }
        if (urlSubCat) {
          setSelectedCategories(prev => [...prev, urlSubCat]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read normalized filters from URL on mount/URL change (SEO-friendly shareable links)
  useEffect(() => {
    const dim = searchParams.getAll('dimension');
    const mat = searchParams.getAll('material');
    const pc = searchParams.getAll('tryckklass');
    setSelectedDimensions(dim);
    setSelectedMaterials(mat);
    setSelectedPressureClasses(pc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Smart filter normalization (handles synonyms like PN/Tryckklass/Pressure Class)
  const activeFilters = {
    dimension: selectedDimensions,
    material: selectedMaterials,
    pressureClass: selectedPressureClasses,
  };

  // Build options with counts per facet (e-commerce style)
  const filterOptions = buildFilterOptions(products, activeFilters);
  const dimensions = filterOptions.dimension || [];
  const materials = filterOptions.material || [];
  const pressureClasses = filterOptions.pressureClass || [];

  // Build a lookup map for category slugs and names
  const categoryLookup = {};
  categories.forEach(cat => {
    categoryLookup[cat.id] = cat;
    categoryLookup[cat.slug?.toLowerCase()] = cat;
    categoryLookup[cat.name_sv?.toLowerCase()] = cat;
    cat.children?.forEach(sub => {
      categoryLookup[sub.id] = sub;
      categoryLookup[sub.slug?.toLowerCase()] = sub;
      categoryLookup[sub.name_sv?.toLowerCase()] = sub;
    });
  });

  // Filter products using normalized matching
  const filteredProducts = products.filter(product => {
    if (!productMatchesFilters(product, activeFilters)) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = language === 'sv' ? product.name_sv : product.name_en;
      const desc = language === 'sv' ? product.description_sv : product.description_en;
      const meta = language === 'sv' ? product.meta_sv : product.meta_en;
      const matchesName = name?.toLowerCase().includes(query);
      const matchesDesc = desc?.toLowerCase().includes(query);
      const matchesMeta = meta?.toLowerCase().includes(query);
      if (!matchesName && !matchesDesc && !matchesMeta) return false;
    }
    return true;
  });

  // URL key for each filter group (Swedish for SEO)
  const URL_KEYS = { dimension: 'dimension', material: 'material', pressureClass: 'tryckklass' };

  const writeFiltersToUrl = (next) => {
    const newParams = new URLSearchParams(searchParams);
    Object.keys(URL_KEYS).forEach((groupKey) => {
      newParams.delete(URL_KEYS[groupKey]);
      (next[groupKey] || []).forEach((v) => newParams.append(URL_KEYS[groupKey], v));
    });
    setSearchParams(newParams, { replace: true });
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedDimensions([]);
    setSelectedMaterials([]);
    setSelectedPressureClasses([]);
    setSearchQuery('');
    setSearchParams({});
  };

  const toggleFilter = (type, value) => {
    const setters = {
      category: setSelectedCategories,
      dimension: setSelectedDimensions,
      material: setSelectedMaterials,
      pressureClass: setSelectedPressureClasses
    };
    const current = {
      category: selectedCategories,
      dimension: selectedDimensions,
      material: selectedMaterials,
      pressureClass: selectedPressureClasses
    };
    
    const setter = setters[type];
    const currentValues = current[type];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setter(nextValues);

    // Sync to URL (filter groups only — categories handled separately)
    if (URL_KEYS[type]) {
      writeFiltersToUrl({ ...current, [type]: nextValues });
    }
  };

  const removeFilterValue = (type, value) => {
    toggleFilter(type, value);
  };

  const toggleVariants = (productId) => {
    setExpandedVariants(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const hasActiveFilters = selectedDimensions.length > 0 || selectedMaterials.length > 0 || 
                          selectedPressureClasses.length > 0 || searchQuery;

  const activeFilterCount = selectedDimensions.length + selectedMaterials.length + 
                           selectedPressureClasses.length;

  // ============== SEO: avoid duplicate content from facet/filter URLs ==============
  // Industry best practice: noindex any filtered/searched view (Google only indexes the base
  // catalog + canonical category pages). Faceted URLs are useful for users (shareable) but
  // shouldn't compete with primary URLs in search results.
  const hasFilteredView = activeFilterCount > 0 || (searchQuery && searchQuery.trim() !== '');

  // Dynamic browser-tab title (still helpful for users) but base canonical URL
  const filterText = activeFiltersToText({
    dimension: selectedDimensions,
    material: selectedMaterials,
    pressureClass: selectedPressureClasses,
  });
  const seoTitle = filterText
    ? (language === 'sv'
      ? `${filterText} – VVS-produkter | Flowingo`
      : `${filterText} – HVAC Products | Flowingo`)
    : (language === 'sv'
      ? 'VVS-produkter | Mätare, expansionssystem, tankar & mer'
      : 'HVAC Products | Meters, Expansion Systems, Tanks & More');
  const seoDescription = filterText
    ? (language === 'sv'
      ? `Hitta VVS-produkter som matchar ${filterText}. ${filteredProducts.length} produkter från Flowingo med filtrering på dimension, material och tryckklass.`
      : `Find HVAC products matching ${filterText}. ${filteredProducts.length} products from Flowingo.`)
    : (language === 'sv'
      ? 'Upptäck Flowingos kompletta sortiment av VVS-produkter. Filtrera efter dimension, material och tryckklass.'
      : 'Discover Flowingo\'s complete range of HVAC products. Filter by dimension, material and pressure class.');
  // ALWAYS canonical to the base catalog — never the filtered URL
  const canonicalUrl = 'https://flowingo.se/produkter';

  return (
    <div data-testid="products-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        url={canonicalUrl}
        language={language}
        noindex={hasFilteredView}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Produkter' : 'Products', url: 'https://flowingo.se/produkter' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': 'https://flowingo.se/produkter',
            name: filterText ? `${filterText} – VVS-produkter` : (language === 'sv' ? 'VVS-produktkatalog' : 'HVAC Product Catalog'),
            description: seoDescription,
            isPartOf: { '@id': 'https://flowingo.se/#website' },
            about: {
              '@type': 'Thing',
              name: 'VVS-produkter',
            },
            mainEntity: {
              '@type': 'ItemList',
              name: 'VVS-produktkategorier',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Mätare', url: 'https://flowingo.se/produkter/kategori/matare' },
                { '@type': 'ListItem', position: 2, name: 'Expansionssystem', url: 'https://flowingo.se/produkter/kategori/expansionssystem' },
                { '@type': 'ListItem', position: 3, name: 'Tankar', url: 'https://flowingo.se/produkter/kategori/tankar' },
                { '@type': 'ListItem', position: 4, name: 'Pumpar', url: 'https://flowingo.se/produkter/kategori/pumpar' },
                { '@type': 'ListItem', position: 5, name: 'Ventiler', url: 'https://flowingo.se/produkter/kategori/ventiler' },
                { '@type': 'ListItem', position: 6, name: 'Värmeväxlare', url: 'https://flowingo.se/produkter/kategori/varmevaxlare' },
                { '@type': 'ListItem', position: 7, name: 'Återströmningsskydd', url: 'https://flowingo.se/produkter/kategori/aterstromning' },
                { '@type': 'ListItem', position: 8, name: 'Luft- och partikelavskiljare', url: 'https://flowingo.se/produkter/kategori/luft-partikelavskiljare' },
                { '@type': 'ListItem', position: 9, name: 'Automatisering och manövrering', url: 'https://flowingo.se/produkter/kategori/automatisering-manovrering' },
              ]
            }
          }
        ]}
      />
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-4">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">{t('nav.products')}</span>
        </nav>
        
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-4">
          {language === 'sv' 
            ? 'VVS-produkter: Mätare, expansionssystem, tankar & mer' 
            : 'HVAC Products: Meters, Expansion Systems, Tanks & More'}
        </h1>
        
        <p className="text-stone-600 mb-6">
          {language === 'sv' 
            ? 'Flowingo är specialister på mätare, expansionssystem och tankar för VVS-branschen. Vi erbjuder även ett komplett sortiment av pumpar, ventiler, värmeväxlare, återströmningsskydd, luft- och partikelavskiljare samt automatisering och manövrering. Perfekt för installatörer, grossister och fastighetsbolag. Filtrera efter dimension, material och tryckklass.'
            : 'Flowingo specializes in meters, expansion systems and tanks for the HVAC industry. We also offer a complete range of pumps, valves, heat exchangers, backflow preventers, air and particle separators, and automation. Perfect for installers, wholesalers and property companies. Filter by dimension, material and pressure class.'
          }
        </p>

        {/* Category Pills - Now as links to dedicated pages */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(cat => (
            <Link key={cat.id} to={`/produkter/kategori/${cat.slug}`}>
              <button
                className="px-4 py-2 text-sm border transition-colors bg-white text-stone-700 border-stone-300 hover:border-stone-400 hover:bg-stone-50"
              >
                {language === 'sv' ? cat.name_sv : cat.name_en}
              </button>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Filter Bar */}
        <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-stone-200">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Removed category filter - categories are now navigation links, not filters */}
            <FilterDropdown
              label={language === 'sv' ? FILTER_GROUPS.dimension.label_sv : FILTER_GROUPS.dimension.label_en}
              options={dimensions.map(d => ({ value: d.value, label: d.value, count: d.count }))}
              selected={selectedDimensions}
              onToggle={(val) => toggleFilter('dimension', val)}
            />
            <FilterDropdown
              label={language === 'sv' ? FILTER_GROUPS.material.label_sv : FILTER_GROUPS.material.label_en}
              options={materials.map(m => ({ value: m.value, label: m.value, count: m.count }))}
              selected={selectedMaterials}
              onToggle={(val) => toggleFilter('material', val)}
            />
            <FilterDropdown
              label={language === 'sv' ? FILTER_GROUPS.pressureClass.label_sv : FILTER_GROUPS.pressureClass.label_en}
              options={pressureClasses.map(p => ({ value: p.value, label: p.value, count: p.count }))}
              selected={selectedPressureClasses}
              onToggle={(val) => toggleFilter('pressureClass', val)}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <ProductSearchButton
              language={language}
              initialQuery={searchQuery}
              onSubmit={(q) => {
                setSearchQuery(q);
                const next = new URLSearchParams(searchParams);
                if (q) {
                  next.set('search', q);
                } else {
                  next.delete('search');
                }
                setSearchParams(next);
              }}
            />

            <Button
              variant="outline"
              onClick={() => setShowFilterPanel(true)}
              className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-none flex items-center gap-2"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {language === 'sv' ? 'Se alla filter' : 'See all filters'}
            </Button>
          </div>
        </div>

        {/* Active filter chips (SEO + UX) */}
        {hasActiveFilters && (
          <div
            className="mb-4 flex items-center gap-2 flex-wrap"
            data-testid="active-filter-chips"
            aria-label={language === 'sv' ? 'Aktiva filter' : 'Active filters'}
          >
            <span className="text-xs text-stone-500 uppercase tracking-wider">
              {language === 'sv' ? 'Filter:' : 'Filters:'}
            </span>
            {Object.entries({ dimension: selectedDimensions, material: selectedMaterials, pressureClass: selectedPressureClasses }).map(([groupKey, values]) => (
              values.map((v) => (
                <button
                  key={`${groupKey}-${v}`}
                  type="button"
                  onClick={() => removeFilterValue(groupKey, v)}
                  data-testid={`filter-chip-${groupKey}-${v}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#321a27] text-white text-xs hover:bg-[#4a2d3d] transition-colors group"
                >
                  <span className="text-white/60">
                    {language === 'sv' ? FILTER_GROUPS[groupKey].label_sv : FILTER_GROUPS[groupKey].label_en}:
                  </span>
                  <span className="font-medium">{v}</span>
                  <X className="w-3 h-3 group-hover:scale-110 transition-transform" />
                </button>
              ))
            ))}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  const next = new URLSearchParams(searchParams);
                  next.delete('search');
                  setSearchParams(next);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-stone-700 text-white text-xs hover:bg-stone-800 transition-colors group"
              >
                <span className="text-white/60">{language === 'sv' ? 'Sök' : 'Search'}:</span>
                <span className="font-medium">"{searchQuery}"</span>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Results count */}
        <div className="mb-6 text-stone-600 text-sm flex items-center justify-between">
          <span>
            <strong>{filteredProducts.length}</strong> {language === 'sv' ? 'produkter' : 'products'}
            {hasActiveFilters && filteredProducts.length === products.length === false && (
              <span className="text-stone-400"> {language === 'sv' ? `av ${products.length} totalt` : `of ${products.length} total`}</span>
            )}
          </span>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-[#321a27] hover:underline flex items-center gap-1">
              <X className="w-4 h-4" /> {language === 'sv' ? 'Rensa alla filter' : 'Clear all filters'}
            </button>
          )}
        </div>

        {/* Products List - Ahlsell Style */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white h-48 animate-pulse border border-stone-200" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 bg-white border border-stone-200">
            <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-600">{t('products.noProducts')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => (
              <ProductCard 
                key={product.id}
                product={product}
                language={language}
                t={t}
                isExpanded={expandedVariants[product.id]}
                onToggleVariants={() => toggleVariants(product.id)}
                categories={categories}
              />
            ))}
          </div>
        )}
      </div>

      {/* Filter Panel (Ahlsell-style slide-in) */}
      {showFilterPanel && (
        <FilterPanel
          language={language}
          categories={categories}
          dimensions={dimensions}
          materials={materials}
          pressureClasses={pressureClasses}
          selectedCategories={selectedCategories}
          selectedDimensions={selectedDimensions}
          selectedMaterials={selectedMaterials}
          selectedPressureClasses={selectedPressureClasses}
          expandedFilters={expandedFilters}
          setExpandedFilters={setExpandedFilters}
          toggleFilter={toggleFilter}
          clearFilters={clearFilters}
          onClose={() => setShowFilterPanel(false)}
          resultCount={filteredProducts.length}
        />
      )}
    </div>
  );
};

// Filter Dropdown Component
const FilterDropdown = ({ label, options, selected, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-2 text-sm border flex items-center gap-2 ${
          selected.length > 0 
            ? 'bg-[#f5f0f2] border-[#d4c4cc] text-[#321a27]' 
            : 'bg-white border-stone-300 text-stone-700 hover:border-stone-400'
        }`}
      >
        {label} {selected.length > 0 && `(${selected.length})`}
        <ChevronDown className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-[240px] max-h-64 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-3 text-xs text-stone-400 text-center">Inga tillgängliga</div>
            ) : (
              options.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer"
                  data-testid={`filter-option-${opt.value}`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selected.includes(opt.value)}
                      onCheckedChange={() => onToggle(opt.value)}
                    />
                    <span className="text-sm text-stone-700">{opt.label}</span>
                  </div>
                  {typeof opt.count === 'number' && (
                    <span className="text-xs text-stone-400 font-mono">({opt.count})</span>
                  )}
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Product Card with expandable variants (Ahlsell-style)
const ProductCard = ({ product, language, t, isExpanded, onToggleVariants, categories }) => {
  const { addToQuote } = useQuote();
  const productName = language === 'sv' ? product.name_sv : product.name_en;
  const productDescription = language === 'sv' ? product.description_sv : product.description_en;
  const variantCount = product.variants?.length || 0;
  
  // Get main category and subcategory names
  const mainCatId = product.main_category_id || product.category;
  const subCatId = product.sub_category_id;
  
  let mainCategoryName = '';
  let subCategoryName = '';
  
  for (const cat of categories) {
    if (cat.id === mainCatId || cat.slug === mainCatId) {
      mainCategoryName = language === 'sv' ? cat.name_sv : cat.name_en;
      if (subCatId) {
        for (const sub of cat.children || []) {
          if (sub.id === subCatId || sub.slug === subCatId) {
            subCategoryName = language === 'sv' ? sub.name_sv : sub.name_en;
            break;
          }
        }
      }
      break;
    }
  }
  
  const categoryDisplay = subCategoryName 
    ? `${mainCategoryName} / ${subCategoryName}` 
    : mainCategoryName || mainCatId;

  return (
    <div className="bg-white border border-stone-200">
      {/* Main Product Info */}
      <div className="p-4 flex gap-4">
        {/* Image */}
        <div className="w-36 h-36 bg-white flex-shrink-0 overflow-hidden flex items-center justify-center">
          {product.image_url ? (
            <img 
              src={productImage(product.image_url)} 
              alt={productName} 
              width="144" height="144"
              className="w-full h-full object-contain" 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-400">
              <Package className="w-12 h-12" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
            {categoryDisplay}
          </div>
          <Link 
            to={`/produkter/${product.slug || product.id}`}
            className="text-lg font-semibold text-stone-900 hover:text-[#321a27] block mb-1"
          >
            {productName}
          </Link>
          <p className="text-stone-600 text-sm line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: productDescription || '' }} />
          
          {/* Variants button */}
          {variantCount > 0 && (
            <button
              onClick={onToggleVariants}
              className="px-4 py-2 text-sm border border-[#321a27] text-[#321a27] hover:bg-[#f5f0f2] flex items-center gap-2"
            >
              {isExpanded ? (
                <>
                  {language === 'sv' ? 'Dölj varianter' : 'Hide variants'} ({variantCount})
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  {language === 'sv' ? 'Visa varianter' : 'Show variants'} ({variantCount})
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Expanded Variants Table */}
      {isExpanded && product.variants?.length > 0 && (
        <div className="border-t border-stone-200">
          <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
            <h4 className="font-semibold text-stone-900">
              {language === 'sv' ? 'Varianter' : 'Variants'}
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#321a27] text-white">
                <tr>
                  {/* Dynamic columns based on variant_columns or first variant keys */}
                  {(product.variant_columns || Object.keys(product.variants[0] || {})).map((col) => (
                    <th key={col} className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                      {col.replace(/_/g, ' ')}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-sm font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {product.variants.map((variant, idx) => {
                  // Get artikelnummer for this variant
                  const artikelnummer = variant.Artikelnummer || variant.artikelnummer || variant['Artikelnummer'] || '';
                  
                  return (
                    <tr 
                      key={idx} 
                      className={`border-b border-stone-200 hover:bg-[#f5f0f2] ${idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}
                    >
                      {/* Dynamic cells */}
                      {(product.variant_columns || Object.keys(product.variants[0] || {})).map((col, colIdx) => {
                        const isArtikelnummer = col.toLowerCase() === 'artikelnummer';
                        const cellValue = variant[col] !== undefined ? String(variant[col]) : '-';
                        
                        if (isArtikelnummer) {
                          return (
                            <td key={col} className="px-4 py-3 text-sm">
                              <Link 
                                to={`/produkter/${product.slug || product.id}?variant=${encodeURIComponent(cellValue)}`}
                                className="text-[#321a27] font-medium hover:underline"
                              >
                                {cellValue}
                              </Link>
                            </td>
                          );
                        }
                        
                        return (
                          <td key={col} className="px-4 py-3 text-sm text-stone-700">
                            {cellValue}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right">
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addToQuote(product, variant, 1);
                            toast.success(language === 'sv' ? 'Tillagd i offertlistan!' : 'Added to quote list!');
                          }}
                          className="px-3 py-1 bg-[#321a27] text-white text-sm hover:bg-[#4a2d3d] inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          {language === 'sv' ? 'Offertlista' : 'Quote'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Filter Panel (Ahlsell-style slide-in from right)
const FilterPanel = ({
  language,
  categories,
  dimensions,
  materials,
  pressureClasses,
  selectedCategories,
  selectedDimensions,
  selectedMaterials,
  selectedPressureClasses,
  expandedFilters,
  setExpandedFilters,
  toggleFilter,
  clearFilters,
  onClose,
  resultCount
}) => {
  const toggleSection = (section) => {
    setExpandedFilters(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const FilterSection = ({ id, title, options, selected, type }) => (
    <div className="border-b border-stone-200">
      <button
        onClick={() => toggleSection(id)}
        className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-stone-50"
      >
        <span className="font-medium text-stone-900">
          {title}
          {selected.length > 0 && (
            <span className="ml-2 text-xs text-[#321a27] font-semibold">({selected.length})</span>
          )}
        </span>
        {expandedFilters[id] ? (
          <ChevronUp className="w-5 h-5 text-stone-500" />
        ) : (
          <span className="text-stone-400 text-xl">+</span>
        )}
      </button>
      {expandedFilters[id] && (
        <div className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto">
          {options.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">
              {language === 'sv' ? 'Inga tillgängliga' : 'None available'}
            </p>
          ) : (
            options.map(opt => (
              <label key={opt.value} className="flex items-center justify-between gap-3 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selected.includes(opt.value)}
                    onCheckedChange={() => toggleFilter(type, opt.value)}
                  />
                  <span className="text-sm text-stone-700">{opt.label}</span>
                </div>
                {typeof opt.count === 'number' && (
                  <span className="text-xs text-stone-400 font-mono">({opt.count})</span>
                )}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-4 py-4 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-stone-900">
            {language === 'sv' ? 'Filtrera' : 'Filter'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded">
            <X className="w-6 h-6 text-stone-600" />
          </button>
        </div>

        {/* Filter Sections - Removed category filter */}
        <div className="flex-1 overflow-y-auto">
          <FilterSection
            id="dimension"
            title={language === 'sv' ? 'Dimension' : 'Dimension'}
            options={dimensions.map(d => ({ value: d.value, label: d.value, count: d.count }))}
            selected={selectedDimensions}
            type="dimension"
          />
          <FilterSection
            id="material"
            title={language === 'sv' ? 'Material' : 'Material'}
            options={materials.map(m => ({ value: m.value, label: m.value, count: m.count }))}
            selected={selectedMaterials}
            type="material"
          />
          <FilterSection
            id="pressureClass"
            title={language === 'sv' ? 'Tryckklass (PN)' : 'Pressure Class (PN)'}
            options={pressureClasses.map(p => ({ value: p.value, label: p.value, count: p.count }))}
            selected={selectedPressureClasses}
            type="pressureClass"
          />
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-4 border-t border-stone-200 flex gap-4">
          <button
            onClick={clearFilters}
            className="flex-1 px-4 py-3 border border-stone-300 text-stone-700 hover:bg-stone-50 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            {language === 'sv' ? 'Rensa alla filter' : 'Clear all filters'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-[#321a27] text-white hover:bg-[#4a2d3d]"
          >
            {language === 'sv' ? `Visa ${resultCount} produkter` : `Show ${resultCount} products`}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProductsPage;
